/**
 * Meta (Facebook) Pixel Initialization
 *
 * Loads the Meta Pixel base code and fires the initial PageView event.
 * This runs alongside GTM — the pixel is needed so that fbq() calls
 * from metaTracking.js fire browser-side events for deduplication
 * with the server-side Conversions API events.
 */

import { config } from '../config/env'
import { logger } from './logger'
import { isMetaSource } from './trafficSource'

/**
 * True when Meta tracking (pixel, browser and CAPI events) must be skipped:
 * Meta is only sent when localStorage `source` is "meta".
 * @returns {boolean}
 */
export const isMetaTrackingBlocked = () => !isMetaSource()

/**
 * Initialize the Meta Pixel base code.
 * Safe to call multiple times — guards against double-init.
 */
export const initializeMetaPixel = () => {
  const pixelIds = config.metaPixelIds

  if (pixelIds.length === 0) {
    logger.debug('Meta Pixel not initialized: No pixel ID provided')
    return
  }

  if (isMetaTrackingBlocked()) {
    logger.debug('Meta Pixel not initialized: source is not meta')
    return
  }

  // Note: we intentionally allow pixel initialization even in testMode.
  // testMode is used to gate conversion events elsewhere (e.g. metaTracking.js),
  // but we may still want PageView/pixel availability for QA in staging/test.

  if (window.fbq) {
    logger.debug('Meta Pixel already initialized')
    return
  }

  logger.debug('Initializing Meta Pixel(s):', pixelIds)

  /* eslint-disable */
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  // Every init'd pixel receives all subsequent fbq('track', ...) calls,
  // so a single PageView here fires to each pixel.
  pixelIds.forEach((id) => window.fbq('init', id))
  window.fbq('track', 'PageView')

  logger.debug('Meta Pixel(s) initialized successfully')
}

/**
 * Push PII into the pixel for Advanced Matching (unhashed — Meta hashes client-side).
 * Safe to call before each event once email/phone exist (e.g. Quiz Completed / checkout).
 */
export const syncMetaAdvancedMatching = (funnelData = {}) => {
  const pixelIds = config.metaPixelIds
  if (pixelIds.length === 0 || !window.fbq) return
  if (isMetaTrackingBlocked()) return

  const userData = {}

  if (funnelData.email) {
    userData.em = String(funnelData.email).trim().toLowerCase()
  }
  if (funnelData.phone) {
    const digits = String(funnelData.phone).replace(/\D/g, '')
    if (digits.length === 10) userData.ph = `1${digits}`
    else if (digits.length >= 11) userData.ph = digits
  }
  if (funnelData.first_name) {
    userData.fn = String(funnelData.first_name).trim().toLowerCase()
  }
  if (funnelData.last_name) {
    userData.ln = String(funnelData.last_name).trim().toLowerCase()
  }
  if (funnelData.city || funnelData.shipping_city || funnelData.nad_shipping_city) {
    userData.ct = String(
      funnelData.city || funnelData.shipping_city || funnelData.nad_shipping_city
    ).trim().toLowerCase().replace(/\s/g, '')
  }
  if (funnelData.state || funnelData.shipping_state || funnelData.nad_shipping_state) {
    userData.st = String(
      funnelData.state || funnelData.shipping_state || funnelData.nad_shipping_state
    ).trim().toLowerCase().substring(0, 2)
  }
  if (funnelData.zip || funnelData.postal_code || funnelData.shipping_postal_code || funnelData.nad_shipping_postal_code) {
    userData.zp = String(
      funnelData.zip || funnelData.postal_code || funnelData.shipping_postal_code || funnelData.nad_shipping_postal_code
    ).replace(/\D/g, '').substring(0, 5)
  }
  if (funnelData.country || funnelData.shipping_country || funnelData.nad_shipping_country) {
    userData.country = String(
      funnelData.country || funnelData.shipping_country || funnelData.nad_shipping_country
    ).trim().toLowerCase().substring(0, 2)
  }
  if (funnelData.entry_id) {
    userData.external_id = String(funnelData.entry_id)
  }

  if (Object.keys(userData).length === 0) return

  pixelIds.forEach((id) => window.fbq('init', id, userData))
  logger.debug('[Meta] Advanced matching synced', Object.keys(userData))
}

export default { initializeMetaPixel, syncMetaAdvancedMatching, isMetaTrackingBlocked }
